import React, { FunctionComponent, KeyboardEventHandler, MouseEventHandler, ReactNode, useContext, useRef } from "react";

import {
    AlignItems,
    BooleanSwitcher,
    BooleanSwitcherChildrenProps,
    Icon,
    IconColor,
    Layout,
    SpacingSize,
} from "@multiweb/reactComponents";
import { bemClassNames, createQA, TAB_INDEX_FOCUSABLE_ONLY, usePreventMiddleButtonScroll, useSetFocus } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { QAProps } from "@multiweb/types/react";
import { FORMS_HEADER_HEIGHT, GROUP_HEADER_HEIGHT } from "../../constants";
import { ScrollContext } from "../../context";
import { activationKeyBlockHandler, activationMouseBlockHandler, createActivationKeyHandler, createActivationMouseHandler, createMenuCustomScroll } from "../../utils";
import { MenuItemComponentVariant } from "./types";

export type MenuItemComponentProps = {
    /** Kód ikony zobrazené na levé straně */
    iconCode?: IconCode;
    /** Příznak, zda má být komponenta vizuálně zvýrazněná (komponenta vypadá jako při najetí myši) */
    isActive?: boolean;
    /** Text zobrazovaný ve středové části */
    label: string | undefined;
    /**
     * Callback volaný při kliknutí, klikatelná je celá oblast komponenty kromě takové pravé části,
     * která má vlastní `onClick` callback.
     * Reaguje na primární i ostatní tlačítka myši (přidává se na onClick i onAuxClick).
     */
    onClick: MouseEventHandler;
    /** Funkce pro vytvoření komponenta zobrazované na pravé části položky menu. Jako parametr dostává příznak aktivnosti. */
    rightPartComponentCreator?: (isActive: boolean) => ReactNode;
    /** Příznak, zda je komponenta zaměřená. */
    isFocused: boolean;
    /** Callback volaný při uvolnění klávesy. */
    onKeyUp?: KeyboardEventHandler;
    /** Varianta, zatím použitá pro odlišení položek userMenu. */
    variant?: MenuItemComponentVariant;
} & QAProps;

const classNames = bemClassNames("menu-item-component");

/**
 * Obecná komponenta pro zobrazení položky v menu (neslouží pro notifikace). Podporuje zobrazení ikony,
 * textu a konfigurovatelné pravé části (typicky použité pro kontextové hover menu).
 * */
export const MenuItemComponent: FunctionComponent<MenuItemComponentProps> = ({
    iconCode,
    isActive = false,
    label = "",
    onClick,
    rightPartComponentCreator,
    isFocused,
    onKeyUp,
    qa,
    variant = MenuItemComponentVariant.default,
}) => {
    const ref = useRef<HTMLElement>(null);
    usePreventMiddleButtonScroll(ref);
    const scrollTo = useContext(ScrollContext);
    useSetFocus(isFocused, ref, createMenuCustomScroll(scrollTo, FORMS_HEADER_HEIGHT + GROUP_HEADER_HEIGHT));
    const handleClick = createActivationMouseHandler(onClick);
    const handleKeyUp = createActivationKeyHandler(onKeyUp);

    return (
        <BooleanSwitcher>
            {({ value: isHovered, turnOn: onMouseEnter, turnOff: onMouseLeave }: BooleanSwitcherChildrenProps) => {
                const isActiveOrHovered = isHovered || isActive;
                // Nebyl použit element `button`, neboť není určen pro funkci flex kontejneru
                // @see https://github.com/rachelandrew/gridbugs#10-some-html-elements-cant-be-grid-containers
                return (
                    <span
                        role="button"
                        className={classNames({ active: isActiveOrHovered, variant })}
                        onFocus={onMouseEnter}
                        onBlur={onMouseLeave}
                        onClick={handleClick}
                        onAuxClick={handleClick}
                        onMouseDown={activationMouseBlockHandler}
                        onKeyUp={handleKeyUp}
                        onKeyDown={activationKeyBlockHandler}
                        tabIndex={TAB_INDEX_FOCUSABLE_ONLY}
                        {...{ onMouseEnter, onMouseLeave, ref }}
                        {...createQA(qa)}
                    >
                        {iconCode ? (
                            <Layout
                                spacingSizeHorizontal={SpacingSize.m}
                                alignItems={AlignItems.center}
                            >
                                <Icon
                                    color={isActiveOrHovered ? IconColor.whiteFixed : IconColor.whiteSecondaryFixed}
                                    {...{ iconCode }}
                                />
                            </Layout>
                        ) : (
                            <Layout spacingSize={SpacingSize.m} />
                        )}
                        <span className={classNames("label", { active: isActiveOrHovered })}>
                            {label}
                        </span>
                        {rightPartComponentCreator && (
                            rightPartComponentCreator(isActiveOrHovered)
                        )}
                    </span>
                );
            }}
        </BooleanSwitcher>
    );
};
